// M24 — Advancement toasts + tree screen (UI). Pure presentation (ENG-1): the
// engine (sim/game/advancements) decides what is granted; this renders the toast
// pop-ups and the L-key tree browser. Icons come from the shared ItemIcons cache
// via an item-name→data-URL lookup so no image assets are needed.
import {
  ADVANCEMENT_BY_ID,
  ADVANCEMENTS,
  ROOT_ADVANCEMENTS,
  type AdvancementDef,
  type AdvancementTracker,
} from '../../sim/game/advancements';

/** Item-name → icon data URL (backed by ItemIcons; '' if unknown). */
export type IconUrl = (item: string) => string;

const TOAST_MS = 5000;

/** Top-right advancement pop-ups. One per granted advancement, auto-dismissed. */
export class AdvancementToasts {
  readonly el = document.createElement('div');
  private readonly log: string[] = [];

  constructor(private readonly iconUrl: IconUrl) {
    this.el.className = 'bb-toasts';
    document.body.append(this.el);
  }

  /** Pop a toast for a newly-granted advancement id (no-op for an unknown id). */
  show(id: string): void {
    const def = ADVANCEMENT_BY_ID.get(id);
    if (!def) return;
    this.log.push(def.title);

    const toast = document.createElement('div');
    toast.className = `bb-toast bb-toast-${def.frame}`;

    const icon = document.createElement('div');
    icon.className = 'bb-toast-icon';
    const url = this.iconUrl(def.icon);
    if (url) icon.style.backgroundImage = `url(${url})`;

    const body = document.createElement('div');
    body.className = 'bb-toast-body';
    const head = document.createElement('div');
    head.className = 'bb-toast-head';
    head.textContent = def.frame === 'challenge' ? 'Challenge Complete!' : 'Advancement Made!';
    const title = document.createElement('div');
    title.className = 'bb-toast-title';
    title.textContent = def.title;
    body.append(head, title);

    toast.append(icon, body);
    this.el.append(toast);
    setTimeout(() => toast.remove(), TOAST_MS);
  }

  /** A generic toast not tied to an advancement (blueprint complete, etc., UI-7).
   *  Reuses the same pop-up stack + styling as advancement toasts. */
  note(head: string, title: string, iconUrl?: string): void {
    this.log.push(title);
    const toast = document.createElement('div');
    toast.className = 'bb-toast bb-toast-goal';
    const icon = document.createElement('div');
    icon.className = 'bb-toast-icon';
    if (iconUrl) icon.style.backgroundImage = `url(${iconUrl})`;
    const body = document.createElement('div');
    body.className = 'bb-toast-body';
    const headEl = document.createElement('div');
    headEl.className = 'bb-toast-head';
    headEl.textContent = head;
    const titleEl = document.createElement('div');
    titleEl.className = 'bb-toast-title';
    titleEl.textContent = title;
    body.append(headEl, titleEl);
    toast.append(icon, body);
    this.el.append(toast);
    setTimeout(() => toast.remove(), TOAST_MS);
  }

  /** Titles toasted so far (test/E2E seam). */
  shown(): readonly string[] {
    return this.log.slice();
  }
}

/** The advancement tree browser (L). Tabs per root; nodes show done/locked state. */
export function showAdvancements(tracker: AdvancementTracker, iconUrl: IconUrl, onClose: () => void): HTMLElement {
  const screen = document.createElement('div');
  screen.className = 'bb-screen bb-pause bb-adv-screen';

  const panel = document.createElement('div');
  panel.className = 'bb-panel bb-adv-panel';

  const title = document.createElement('div');
  title.className = 'bb-adv-title';
  const total = ADVANCEMENTS.length;
  const done = ADVANCEMENTS.filter((d) => tracker.has(d.id)).length;
  title.textContent = `Advancements — ${done}/${total}`;

  const tabs = document.createElement('div');
  tabs.className = 'bb-creative-tabs';
  const tree = document.createElement('div');
  tree.className = 'bb-adv-tree';

  const renderTab = (root: AdvancementDef): void => {
    tree.replaceChildren();
    // The whole subtree of this root, indented by depth (BFS keeps parents first).
    const rows: { def: AdvancementDef; depth: number }[] = [];
    const walk = (def: AdvancementDef, depth: number): void => {
      rows.push({ def, depth });
      for (const child of ADVANCEMENTS.filter((d) => d.parent === def.id)) walk(child, depth + 1);
    };
    walk(root, 0);

    for (const { def, depth } of rows) {
      const row = document.createElement('div');
      const complete = tracker.has(def.id);
      row.className = `bb-adv-row bb-adv-${def.frame}${complete ? ' bb-adv-done' : ''}`;
      row.style.marginLeft = `${depth * 18}px`;

      const icon = document.createElement('div');
      icon.className = 'bb-adv-icon';
      const url = iconUrl(def.icon);
      if (url) icon.style.backgroundImage = `url(${url})`;

      const text = document.createElement('div');
      text.className = 'bb-adv-text';
      const name = document.createElement('div');
      name.className = 'bb-adv-name';
      name.textContent = def.title;
      const desc = document.createElement('div');
      desc.className = 'bb-adv-desc';
      desc.textContent = complete ? def.description : `${def.description} — ${Math.round(tracker.progress(def.id) * 100)}%`;
      text.append(name, desc);

      row.append(icon, text);
      tree.append(row);
    }
  };

  for (const root of ROOT_ADVANCEMENTS) {
    const tab = document.createElement('button');
    tab.className = 'bb-tab';
    tab.textContent = root.title;
    tab.onclick = () => {
      for (const t of tabs.children) t.classList.remove('bb-tab-active');
      tab.classList.add('bb-tab-active');
      renderTab(root);
    };
    tabs.append(tab);
  }
  (tabs.firstElementChild as HTMLButtonElement | null)?.click();

  const close = document.createElement('button');
  close.className = 'bb-btn';
  close.textContent = 'Close';
  const dismiss = (): void => {
    screen.remove();
    onClose();
  };
  close.onclick = dismiss;
  screen.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' || e.key === 'l' || e.key === 'L') dismiss();
  });

  panel.append(title, tabs, tree, close);
  screen.append(panel);
  document.body.append(screen);
  screen.tabIndex = -1;
  screen.focus();
  return screen;
}
